import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Wallet, 
  CreditCard, 
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResidentBalancePage() {
  const { user, isSimulationActive, stopRoleSimulation } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [card, setCard] = useState(null);
  const [balanceRequests, setBalanceRequests] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestNote, setRequestNote] = useState('');
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  
  // Simülasyonu bitir
  const handleStopSimulation = () => {
    if (stopRoleSimulation) {
      stopRoleSimulation();
      navigate('/dashboard');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');

      // Kullanıcının RFID kartını al
      const cardsResponse = await fetch(`${API_URL}/api/rfid/user/${user?.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (cardsResponse.ok) {
        const cards = await cardsResponse.json();
        setCard(cards?.[0] || null);
      }

      // Bakiye talepleri varsa al (özel endpoint varsa)
      try {
        const requestsResponse = await fetch(`${API_URL}/api/balance-requests?user_id=${user?.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (requestsResponse.ok) {
          const requests = await requestsResponse.json();
          setBalanceRequests(requests || []);
        }
      } catch {
        // Endpoint yoksa boş bırak
      }
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id, fetchData]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!requestAmount || parseFloat(requestAmount) <= 0) {
      toast.error('Geçerli bir tutar giriniz');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('hims_token');

      const response = await fetch(`${API_URL}/api/balance-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user?.id,
          card_id: card?.card_id,
          amount: parseFloat(requestAmount),
          note: requestNote
        })
      });

      if (response.ok) {
        toast.success('Bakiye yükleme talebi gönderildi');
        setShowRequestForm(false);
        setRequestAmount('');
        setRequestNote('');
        fetchData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Talep gönderilemedi');
      }
    } catch (err) {
      // API endpoint yoksa bilgilendirme mesajı göster
      toast.info('Bakiye yükleme talebi yöneticinize iletilecektir');
      setShowRequestForm(false);
      setRequestAmount('');
      setRequestNote('');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Bekliyor', icon: Clock, class: 'bg-amber-100 text-amber-700' },
      'approved': { label: 'Onaylandı', icon: CheckCircle, class: 'bg-emerald-100 text-emerald-700' },
      'rejected': { label: 'Reddedildi', icon: XCircle, class: 'bg-red-100 text-red-700' }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const quickAmounts = [50, 100, 200, 500];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resident-balance">
      {/* Simülasyon Modu Banner */}
      {simulationActive && (
        <div className="bg-red-600 text-white py-3 px-4 rounded-xl flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 animate-pulse" />
            <span className="font-bold">SİMÜLASYON MODU:</span>
            <span className="font-medium">Site Sakini görünümü</span>
          </div>
          <button
            onClick={handleStopSimulation}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
            data-testid="stop-simulation-balance-btn"
          >
            <EyeOff className="w-4 h-4" />
            Simülasyonu Bitir
          </button>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Bakiyem</h1>
        <p className="text-slate-500 dark:text-slate-400">Bakiye durumunuzu görüntüleyin ve yükleme talebi gönderin</p>
      </div>

      {/* Bakiye Kartı */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-blue-100 text-sm mb-1">Mevcut Bakiye</p>
            <p className="text-4xl font-bold">
              {card ? `${(card.balance || 0).toFixed(2)} TL` : '0.00 TL'}
            </p>
          </div>
          <div className="p-3 bg-white/20 rounded-xl">
            <Wallet className="w-8 h-8" />
          </div>
        </div>
        
        {card && (
          <div className="flex items-center justify-between pt-4 border-t border-white/20">
            <div>
              <p className="text-blue-100 text-xs">Kart Numarası</p>
              <p className="font-mono text-lg">{card.card_id}</p>
            </div>
            <div className="text-right">
              <p className="text-blue-100 text-xs">Kart Tipi</p>
              <p className="text-sm font-medium">
                {card.card_type === 'prepaid' ? 'Ön Ödemeli' : 'Faturalı'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hızlı Yükleme Butonları */}
      {card && card.card_type === 'prepaid' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">Hızlı Bakiye Yükleme</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {quickAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => {
                  setRequestAmount(amount.toString());
                  setShowRequestForm(true);
                }}
                className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-500 border border-transparent transition-all text-center"
              >
                <span className="text-xl font-bold text-slate-800 dark:text-white">{amount} TL</span>
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowRequestForm(!showRequestForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Özel Tutar Gir
          </button>
        </div>
      )}

      {/* Talep Formu */}
      {showRequestForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">Bakiye Yükleme Talebi</h2>
          
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Yüklenecek Tutar (TL)
              </label>
              <input
                type="number"
                value={requestAmount}
                onChange={(e) => setRequestAmount(e.target.value)}
                placeholder="Örn: 100"
                min="1"
                step="0.01"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Not (Opsiyonel)
              </label>
              <textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="Ek bilgi ekleyebilirsiniz..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Talep Gönder
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRequestForm(false);
                  setRequestAmount('');
                  setRequestNote('');
                }}
                className="px-6 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                İptal
              </button>
            </div>
          </form>

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Bakiye yükleme talebiniz site yöneticinize iletilecektir. 
              Onaylandıktan sonra bakiyeniz güncellenecektir.
            </p>
          </div>
        </div>
      )}

      {/* Faturalı Kart Bilgisi */}
      {card && card.card_type === 'postpaid' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <CreditCard className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Faturalı Kart</h3>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Faturalı kartınız için bakiye yükleme gerekmez. Ay sonunda kullanımınız faturalandırılacaktır.
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
                <span className="font-medium">Bu Ayki Kullanım:</span> {(card.monthly_usage || 0).toFixed(2)} TL
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Kart Yok Uyarısı */}
      {!card && (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-8 text-center border border-slate-200 dark:border-slate-700">
          <CreditCard className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
            RFID Kartınız Bulunmuyor
          </h3>
          <p className="text-slate-500">
            Şarj yapabilmek için lütfen site yöneticinizden bir RFID kartı talep edin.
          </p>
        </div>
      )}

      {/* Talep Geçmişi */}
      {balanceRequests.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="p-5 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Talep Geçmişi</h2>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {balanceRequests.map((request, index) => {
              const statusInfo = getStatusBadge(request.status);
              const StatusIcon = statusInfo.icon;
              return (
                <div key={request.id || index} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">
                      {request.amount?.toFixed(2)} TL
                    </p>
                    <p className="text-sm text-slate-500">
                      {request.created_at ? new Date(request.created_at).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.class}`}>
                    <StatusIcon className="w-4 h-4" />
                    {statusInfo.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
